// InlineScriptEditor: inline review and practice editing (React version)
// TODO: Full conversion — this is a working stub

import { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch, store } from '../store';
import { setEditedScriptLines, type EditLine } from '../store/scriptSlice';
import { getWorkingLines } from './OptionsMenu';
import { loadPersistedEdits, loadPersistedRecordings, persistEditedLines,
         getScriptById, updateScript, syncRecordedAudioFromWorkingLines,
         getWorkingCharacterSet } from '../lib/persistence';
import { classifyLine, parseScript } from '../services/scriptParser';
import type { ViewName } from '../app';

function escapeHtml(str: string): string {
    return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

interface InlineScriptEditorProps {
    onNavigate: (view: ViewName) => void;
}

export function InlineScriptEditor({ onNavigate }: InlineScriptEditorProps) {
    const dispatch = useAppDispatch();
    const parsedLines = useAppSelector(s => s.script.parsedLines);

    const [editLines, setEditLines] = useState<EditLine[]>([]);
    const [editHistory, setEditHistory] = useState<EditLine[][]>([]);

    // Build edit lines from working lines
    function buildEditLines() {
        const lines = getWorkingLines();
        const charSet = getWorkingCharacterSet();
        return lines.map(l => {
            const c = classifyLine(l.dialogue || '', charSet);
            const label = l.isStageDirection ? 'STAGE' : (l.character || c.characterGuess || 'STAGE');
            return { label, dialogue: l.dialogue || '', recordedAudioId: l.recordedAudioId || null };
        });
    }

    function handleUndo() {
        if (editHistory.length === 0) return;
        const prev = editHistory[editHistory.length - 1];
        setEditHistory(h => h.slice(0, -1));
        setEditLines(prev);
    }

    function handleSave() {
        // Convert editLines back to ScriptLine format and persist
        const convertedLines = editLines
            .map(l => ({
                character: l.label === 'STAGE' ? null : (l.label || null),
                dialogue: l.dialogue || '',
                isStageDirection: l.label === 'STAGE',
                recordedAudioId: l.recordedAudioId || null
            }))
            .filter(l => l.dialogue);
        dispatch(setEditedScriptLines(convertedLines));
        persistEditedLines();
        onNavigate('practice');
    }

    useEffect(() => {
        if (editLines.length === 0 && getWorkingLines().length > 0) {
            setEditLines(buildEditLines());
        }
    }, [parsedLines]);

    return (
        <div className="character-select-container edit-screen" id="edit-script-view">
            <h1 className="upload-title">Edit Script</h1>
            <p className="upload-subtitle">Refine speaker labels and dialogue before saving this script back to your library.</p>

            <div className="btn-row" style={{ justifyContent: 'space-between', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary btn-small" disabled={editHistory.length === 0} onClick={handleUndo}>Undo</button>
                    <button className="btn btn-secondary btn-small" onClick={() => {
                        setEditHistory(h => [...h, JSON.parse(JSON.stringify(editLines))]);
                        const charSet = getWorkingCharacterSet();
                        setEditLines(editLines.map(l => {
                            const c = classifyLine(l.dialogue, charSet);
                            return { ...l, label: c.type === 'STAGE' ? 'STAGE' : (c.characterGuess || l.label || 'STAGE') };
                        }));
                    }}>Re-run classification</button>
                    <button className="btn btn-secondary btn-small" onClick={() => {
                        setEditHistory(h => [...h, JSON.parse(JSON.stringify(editLines))]);
                        const last = editLines[editLines.length - 1] || { label: 'STAGE' };
                        setEditLines([...editLines, { label: last.label, dialogue: '', recordedAudioId: null }]);
                    }}>Add line</button>
                    <button className="btn btn-secondary btn-small" onClick={() => {
                        if (editLines.length <= 1) return;
                        setEditHistory(h => [...h, JSON.parse(JSON.stringify(editLines))]);
                        setEditLines(editLines.slice(0, -1));
                    }}>Remove line</button>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-small" onClick={() => onNavigate('upload')}>Back</button>
                    <button className="btn btn-primary btn-small" onClick={handleSave}>Save</button>
                </div>
            </div>

            <div className="edit-lines-list" style={{ maxHeight: '60vh' }}>
                {editLines.map((line, i) => (
                    <div key={i} className="edit-line-row" data-index={i}>
                        <div className="edit-line-label" title={line.label || 'STAGE'}>
                            {line.label || 'STAGE'}
                        </div>
                        <div className="edit-line-text-wrap">
                            <textarea
                                className="edit-line-text"
                                value={line.dialogue}
                                onChange={(e) => {
                                    const updated = [...editLines];
                                    updated[i] = { ...updated[i], dialogue: e.target.value };
                                    setEditLines(updated);
                                }}
                            />
                        </div>
                        <div className="edit-line-controls">
                            <button className="btn btn-secondary btn-small" onClick={() => {
                                setEditHistory(h => [...h, JSON.parse(JSON.stringify(editLines))]);
                                const newLine = { label: line.label, dialogue: '', recordedAudioId: null };
                                const updated = [...editLines];
                                updated.splice(i + 1, 0, newLine);
                                setEditLines(updated);
                            }}>+</button>
                            <button className="btn btn-secondary btn-small" onClick={() => {
                                if (editLines.length <= 1) return;
                                setEditHistory(h => [...h, JSON.parse(JSON.stringify(editLines))]);
                                setEditLines(editLines.filter((_, idx) => idx !== i));
                            }}>✕</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
